// Navigations-Katalog für die Seitenleiste — aufgebaut wie im CRM und im
// Event Manager: Kategorien, darin die Seiten. Sichtbarkeit:
//   'alle'        — alle mit einer Mailtool-Rolle
//   'vorbereiten' — wer Mails im Assistenten vorbereitet (alle außer „Nur lesen")
//   'team'        — das Kampagnen-Team (Admin, Redaktion, Nur lesen)
//   'teamEdit'    — Admin und Redaktion
//   'redaktion'   — wer eingereichte Mails freigibt (Admins und Freigaberecht)
//   'admin'       — nur Admins
// Die Datenbank prüft dieselben Regeln selbst; die Seitenleiste blendet nur aus.
import type { ComponentType } from 'react';
import {
  BookOpen, CalendarDays, ClipboardCheck, FileText, Home, Image as ImageIcon, Inbox, Layers,
  LayoutTemplate, Mail, MailPlus, MailX, Settings, ShieldAlert, UserCheck,
} from 'lucide-react';
import type { MailUser } from '../types';
import { istRedaktion, istTeam, kannVorbereiten } from './rollen';

export type NavSicht = 'alle' | 'vorbereiten' | 'team' | 'teamEdit' | 'redaktion' | 'admin';

export type NavEintrag = {
  key: string;
  label: string;
  Icon: ComponentType<{ size?: number }>;
  path: string;
  /** Route-Ende exakt vergleichen (für „/"). */
  end?: boolean;
  sicht: NavSicht;
};

/** Alle Seiten. Reihenfolge hier = Reihenfolge in der Kategorie. */
export const NAV_KATALOG: NavEintrag[] = [
  { key: 'start',         label: 'Start',            Icon: Home,           path: '/',             end: true, sicht: 'alle' },
  { key: 'neue-mail',     label: 'Neue Mail',        Icon: MailPlus,       path: '/neue-mail',    sicht: 'vorbereiten' },
  { key: 'meine-mails',   label: 'Meine Mails',      Icon: Inbox,          path: '/meine-mails',  sicht: 'vorbereiten' },
  { key: 'geplante-mails', label: 'Geplante Mails',  Icon: CalendarDays,   path: '/geplante-mails', sicht: 'alle' },
  { key: 'vorlagen',      label: 'Vorlagen',         Icon: LayoutTemplate, path: '/vorlagen',     sicht: 'alle' },
  { key: 'freigaben',     label: 'Freigaben',        Icon: ClipboardCheck, path: '/freigaben',    sicht: 'redaktion' },
  { key: 'kampagnen',     label: 'Kampagnen',        Icon: Mail,           path: '/campaigns',    sicht: 'team' },
  { key: 'gruppen',       label: 'Gruppen',          Icon: Layers,         path: '/segments',     sicht: 'team' },
  { key: 'templates',     label: 'HTML-Vorlagen',    Icon: FileText,       path: '/templates',    sicht: 'team' },
  { key: 'medien',        label: 'Medien',           Icon: ImageIcon,      path: '/media',        sicht: 'teamEdit' },
  { key: 'kontakte',      label: 'Kontakte',         Icon: UserCheck,      path: '/contacts',     sicht: 'team' },
  { key: 'abmeldungen',   label: 'Abmeldungen',      Icon: MailX,          path: '/abmeldungen',  sicht: 'team' },
  { key: 'qualitaet',     label: 'Datenqualität',    Icon: ShieldAlert,    path: '/data-quality', sicht: 'admin' },
  { key: 'einstellungen', label: 'Einstellungen',    Icon: Settings,       path: '/settings',     sicht: 'admin' },
  { key: 'handbuch',      label: "So funktioniert's", Icon: BookOpen,      path: '/handbuch',     sicht: 'alle' },
];

export type NavKategorie = { id: string; label: string };

export const NAV_KATEGORIEN: NavKategorie[] = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'mails',      label: 'Mails' },
  { id: 'kampagnen',  label: 'Kampagnen' },
  { id: 'kontakte',   label: 'Kontakte' },
  { id: 'verwaltung', label: 'Verwaltung' },
];

/** Seite → Kategorie. */
export const PLATZ: Record<string, string> = {
  start: 'uebersicht',
  handbuch: 'uebersicht',
  'neue-mail': 'mails',
  'meine-mails': 'mails',
  'geplante-mails': 'mails',
  vorlagen: 'mails',
  freigaben: 'mails',
  kampagnen: 'kampagnen',
  gruppen: 'kampagnen',
  templates: 'kampagnen',
  medien: 'kampagnen',
  kontakte: 'kontakte',
  abmeldungen: 'kontakte',
  qualitaet: 'kontakte',
  einstellungen: 'verwaltung',
};

export function sichtbar(e: NavEintrag, u: MailUser | null): boolean {
  if (!u || u.ohneRolle) return false;
  switch (e.sicht) {
    case 'alle': return true;
    case 'vorbereiten': return kannVorbereiten(u);
    case 'team': return istTeam(u);
    case 'teamEdit': return u.role === 'admin' || u.role === 'redakteur';
    case 'redaktion': return istRedaktion(u);
    case 'admin': return u.role === 'admin';
  }
}

/** „Start" heißt für das Team „Dashboard" — dort stehen Zahlen statt Kacheln. */
export function navLabel(e: NavEintrag, u: MailUser | null): string {
  if (e.key === 'start' && istTeam(u)) return 'Dashboard';
  return e.label;
}
